import generateUUID from '../../../_utils/math/generateUUID'

export default class Geometry {
  constructor() {
    this.attributes = {};
    this._attrLocations = {};
    this._glIndexBuffer = null;
    this._glBuffers = {};
    this._uuid = generateUUID();

    this._isDisposed = false;
  }

  get uuid() {
    return this._uuid;
  }

  get isDesposed() {
    return this._isDisposed;
  }

  addAttribute(name, attribute) {
    this.attributes[name] = attribute;
  }

  removeAttribute(name) {
    this.attributes[name] = null;
    delete this.attributes[name];
  }

  setIndex(index) {
    this.index = index;
  }

  removeIndex() {
    this.index = null;
  }

  enableAttributes(glProgram) {
    this._attrLocations = {};
    let attribute, attrLocation;
    for(let name in this.attributes) {
      attribute = this.attributes[name];

      attrLocation = this._gl.getAttribLocation(glProgram, name);
      if(attrLocation == -1) continue;

      this._attrLocations[name] = attrLocation;

      this._gl.bindBuffer(this._gl.ARRAY_BUFFER, this._glBuffers[name]);
      this._gl.enableVertexAttribArray(attrLocation);
      this._gl.vertexAttribPointer(attrLocation, attribute.itemSize, this._gl.FLOAT, false, 0, 0);
    }

    // index
    if(this.index) {
      this._gl.bindBuffer(this._gl.ELEMENT_ARRAY_BUFFER,  this._glIndexBuffer);
    }
  }

  drawGeometry() {
    if(this.index) {
      this._gl.drawElements(this._gl.TRIANGLES, this.index.count, this._gl.UNSIGNED_SHORT, 0);
      this._gl.bindBuffer(this._gl.ELEMENT_ARRAY_BUFFER, this._glIndexBuffer);
    } else {
      this._gl.drawArrays(this._gl.TRIANGLES, 0, 3);
    }
  }

  disableAttributes() {
    this._disableAttrLocations();
  }

  dispose() {
    if(this._isDisposed) return;

    this._disableAttrLocations();
    this._deleteGLBuffers();
    this.attributes = null;
    this._attrLocations = null;
    this._glBuffers = null;
    this._gl = null;

    this._isDisposed = true;
  }

  updateGLBuffers() {
    this._deleteGLBuffers();
    this._createGLBuffers();
  }

  setGLContext(gl) {
    this._gl = gl;
    this._createGLBuffers();
  }

  updateAttribute(name, typedArray) {
    this.attributes[name].array = typedArray;
    this._updateVBO(this._glBuffers[name], typedArray);
  }

  updateIndex(typedArray) {
    this.index = typedArray;
    this._updateIBO(typedArray);
  }

  _disableAttrLocations() {
    for(let name in this._attrLocations) {
      this._disableAttrLocation(this._attrLocations[name]);
    }
    if(this.index) {
      this._gl.bindBuffer(this._gl.ELEMENT_ARRAY_BUFFER, null);
    }
  }

  _disableAttrLocation(attrLocation) {
    this._gl.disableVertexAttribArray(attrLocation);
  }

  _createGLBuffers() {
    let attribute;
    for (let name in this.attributes) {
      attribute = this.attributes[name];
      this._glBuffers[name] = this._createVBO(attribute.array, attribute.dynamic);
    }

    if(this.index) {
      this._glIndexBuffer = this._createIBO(this.index.array, false);
    }
  }

  _deleteGLBuffers() {
    for (let name in this.attributes) {
      this._gl.deleteBuffer(this._glBuffers[name]);
    }
    this._glBuffers = {};
  }

  _createVBO(typedArray, dynamic = false) {
    const drawUsage = dynamic? 'DYNAMIC_DRAW': 'STATIC_DRAW';
    const vbo = this._gl.createBuffer();
    this._gl.bindBuffer(this._gl.ARRAY_BUFFER, vbo);
    this._gl.bufferData(this._gl.ARRAY_BUFFER, typedArray, this._gl[drawUsage]);
    this._gl.bindBuffer(this._gl.ARRAY_BUFFER, null);
    return vbo;
  }

  _updateVBO(vbo, typedArray) {
    this._gl.bindBuffer(this._gl.ARRAY_BUFFER, vbo);
    this._gl.bufferSubData(this._gl.ARRAY_BUFFER, 0, typedArray);
    this._gl.bindBuffer(this._gl.ARRAY_BUFFER, null);
  }

  _createIBO(typedArray, dynamic = false) {
    const drawUsage = dynamic? 'DYNAMIC_DRAW': 'STATIC_DRAW';
    const ibo = this._gl.createBuffer();
    this._gl.bindBuffer(this._gl.ELEMENT_ARRAY_BUFFER, ibo);
    this._gl.bufferData(this._gl.ELEMENT_ARRAY_BUFFER, typedArray, this._gl[drawUsage]);
    this._gl.bindBuffer(this._gl.ELEMENT_ARRAY_BUFFER, null);
    return ibo;
  }

  _updateIBO(typedArray) {
    this._gl.bindBuffer(this._gl.ELEMENT_ARRAY_BUFFER, this._glIndexBuffer);
    this._gl.bufferSubData(this._gl.ELEMENT_ARRAY_BUFFER, 0, typedArray);
    this._gl.bindBuffer(this._gl.ELEMENT_ARRAY_BUFFER, null);
  }
}
